import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {isEmpty} from 'lodash/lang';
import {Table} from 'react-bootstrap';

const defaultHiddenFields = ['id', 'record'];

const buildTable = (records, settings, extraHiddenFields = [], restProps) => {
  const headerRows = [];
  const bodyRows = [];
  const hiddenFields = new Set([...defaultHiddenFields, ...extraHiddenFields]);

  const {onRowClick, sortingManager, onSortableFieldClick} = restProps;
  delete restProps.onRowClick;
  delete restProps.sortingManager;
  delete restProps.onSortableFieldClick;

  for (let fieldName of Object.keys(settings)) {
    if (hiddenFields.has(fieldName)) {
      continue;
    }
    const field = settings[fieldName];
    headerRows.push({
      name: fieldName,
      text: field.fieldText,
      extra: field.fieldExtra || '',
      sortable: field.sortable || false,
    });
    if (field.sortable && !sortingManager.fields[fieldName]) {
      sortingManager.register(fieldName);
    }
  }

  const tableHeader = (
    <thead>
      <tr>
        {headerRows.map((field) => (
          <th
            key={`header-cell-${field.text}`}
            className={classNames({
              'header-cell-sortable': field.sortable,
            })}
            onClick={
              !field.sortable
                ? undefined
                : () =>
                    sortingManager
                      .clickCaret(field.name)
                      .then((sortingFieldStatus) => onSortableFieldClick(field.name, sortingFieldStatus))
            }
          >
            {field.text}
            {field.extra}
            {sortingManager && sortingManager.carets[field.name]}
          </th>
        ))}
      </tr>
    </thead>
  );

  if (isEmpty(records)) {
    return {
      tableHeader,
      tableBody: (
        <tbody>
          <tr>
            <td colSpan={headerRows.length}>No Data</td>
          </tr>
        </tbody>
      ),
    };
  }
  for (let record of records) {
    const row = [];
    for (let [fieldName, fieldValue] of Object.entries(record)) {
      if (hiddenFields.has(fieldName)) {
        continue;
      }
      if (settings[fieldName].hasOwnProperty('formatData')) {
        fieldValue = settings[fieldName].formatData(fieldValue, record, restProps);
      }
      row.push(fieldValue);
    }
    bodyRows.push([record.id, row]);
  }

  const tableBody = (
    <tbody>
      {bodyRows.map(([id, fieldValues]) => (
        <tr
          key={`body-${id}`}
          className={classNames({
            'cursor-pointer': !!onRowClick,
          })}
          onClick={onRowClick ? () => onRowClick(records.find((record) => record.id === id)) : undefined}
        >
          {fieldValues.map((value, idx) => (
            <td key={`body-cell-${idx}`}>{value}</td>
          ))}
        </tr>
      ))}
    </tbody>
  );
  return {tableHeader, tableBody};
};

export default class KluwellTableCore extends React.Component {
  static props = {
    records: PropTypes.object.isRequired,
    settings: PropTypes.object.isRequired,
    hiddenFields: PropTypes.array,
  };
  static defaultProps = {
    hiddenFields: [],
  };

  render() {
    const {records, settings, hiddenFields, ...restProps} = this.props;
    const {tableHeader, tableBody} = buildTable(records, settings, hiddenFields, restProps);
    return (
      <Table hover responsive>
        {tableHeader}
        {tableBody}
      </Table>
    );
  }
}
